import { keysIn, sortBy, uniq } from "lodash";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import type { DataKey } from "recharts/types/util/types";

import useLabel from "@apps/use-label/use-label";
import { BAR_CHART_COLORS } from "@helpers/charts";
import { assertNonNull } from "@helpers/helpers";

import { programTitleTickFormatter } from "./helpers";

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  const isVisible = useMemo(
    () => active && payload && payload.length,
    [active, payload],
  );
  const itemContent = useMemo(() => {
    return sortBy(payload, (item) => item.dataKey).map((item) => {
      const dataKey = assertNonNull(item.dataKey);
      const responderCount = item.payload.counts[dataKey];
      return (
        <div key={dataKey} style={{ color: item.color }}>
          {item.name}: {item.value} ({responderCount}{" "}
          {pluralize("responder", responderCount)})
        </div>
      );
    });
  }, [payload]);
  return (
    <div
      className="custom-tooltip"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
    >
      {isVisible && (
        <>
          <div className="font-medium mb-1">{label}</div>
          {itemContent}
        </>
      )}
    </div>
  );
};

const AvgLineChart = ({
  data,
  labels,
  startValue,
  endValue,
  talentIndicatorName,
}: {
  data: any[];
  startValue: number;
  endValue: number;
  talentIndicatorName: string;
  labels?: string[];
}) => {
  const label = useLabel();
  const [hoveringDataKey, setHoveringDataKey] = useState<DataKey<any> | null>(
    null,
  );

  const handleMouseEnter = useCallback(
    (payload: { dataKey?: DataKey<any> }) => {
      if (payload.dataKey) {
        setHoveringDataKey(payload.dataKey);
      }
    },
    [],
  );
  const handleMouseLeave = useCallback(() => {
    setHoveringDataKey(null);
  }, []);

  const allDataKeys = useMemo(() => {
    return uniq(
      data.reduce((acc, item) => {
        return [
          ...acc,
          ...keysIn(item).filter(
            (k) => !["name", "counts", "labels"].includes(k),
          ),
        ];
      }, []),
    );
  }, [data]);

  return (
    <ResponsiveContainer width="75%" height="100%">
      <LineChart data={data} margin={{ right: 90, left: 90, top: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          label={{
            value: label("review", { pluralize: true, titleCase: true }),
            position: "center",
          }}
          tickFormatter={programTitleTickFormatter}
          height={90}
          interval={0}
          dataKey="name"
        />
        <YAxis
          label={{
            value: talentIndicatorName,
            angle: -90,
            position: "insideLeft",
            dy: 20,
            dx: -20,
          }}
          domain={[startValue, endValue]}
        />
        <Tooltip content={CustomTooltip} />
        <Legend
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        {data.length > 0 &&
          allDataKeys
            .sort()
            .map((key, index) => (
              <Line
                key={key as string}
                dataKey={key as string}
                strokeWidth={3}
                strokeOpacity={
                  hoveringDataKey && hoveringDataKey !== key ? 0.2 : 1
                }
                stroke={BAR_CHART_COLORS[index]}
                name={labels ? (labels[index] ?? undefined) : undefined}
              />
            ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default AvgLineChart;
